import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from "solid-js";
import { draftPattern } from "@tailor-frame/engine";
import { bodyTypes, defaultsFor, specifications, type BodyType } from "@tailor-frame/patterns";
import { PatternThumbnail } from "./PatternThumbnail";
import { audienceForBody, audiences, catalogueSections, collectionFor, garmentType, type Audience, type CatalogueEntry } from "../core/catalogue";
import { linkClick, navigate, routePath } from "../core/router";
import { GROUP_ID, group, groupSet, printSets, setGroup, toggleInGroup } from "../core/printSets";
import { CheckIcon } from "./Icons";

// The library's view choices and last selected pattern, kept between visits.
const libraryStorageKey = "tailor-frame:library";
interface LibraryPreferences {
  grouping: "book" | "type";
  audience: Audience | "all";
  /** The body type the audience was chosen under: a different body starts again from its own patterns. */
  audienceBody?: BodyType;
  showFixtures: boolean;
  selected?: string;
}

function loadPreferences(): LibraryPreferences {
  const fallback: LibraryPreferences = { grouping: "book", audience: "all", showFixtures: false };
  try { return { ...fallback, ...(JSON.parse(localStorage.getItem(libraryStorageKey) ?? "{}") as Partial<LibraryPreferences>) }; } catch { return fallback; }
}

type Direction = "left" | "right" | "up" | "down";
const arrowKeys: Record<string, Direction> = { ArrowLeft: "left", ArrowRight: "right", ArrowUp: "up", ArrowDown: "down" };

/**
 * The tile to move to from `current`. Left and right step through the tiles in reading order, wrapping
 * between rows and sections; up and down go to the nearest row above or below and the closest tile in it.
 */
function neighbour(tiles: HTMLElement[], current: HTMLElement, direction: Direction): HTMLElement | undefined {
  const index = tiles.indexOf(current);
  if (direction === "left") return tiles[index - 1];
  if (direction === "right") return tiles[index + 1];
  const centre = (tile: HTMLElement) => { const rect = tile.getBoundingClientRect(); return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }; };
  const from = centre(current);
  const sign = direction === "down" ? 1 : -1;
  const ahead = tiles.map((tile) => ({ tile, ...centre(tile) })).filter((candidate) => (candidate.y - from.y) * sign > 1);
  if (ahead.length === 0) return undefined;
  const rowY = ahead.reduce((nearest, candidate) => Math.abs(candidate.y - from.y) < Math.abs(nearest - from.y) ? candidate.y : nearest, ahead[0].y);
  return ahead.filter((candidate) => Math.abs(candidate.y - rowY) < 2).sort((a, b) => Math.abs(a.x - from.x) - Math.abs(b.x - from.x))[0]?.tile;
}

/**
 * Pattern select screen: every pattern as a thumbnail tile on a grid, a row per book (or per garment type),
 * chosen with the mouse or the arrow keys, and an inspection panel for the selected pattern.
 */
/** `onSelect` hears the pattern picked among those shown, or undefined when none are, for the goal bar to continue with. */
export function PatternLibrary(props: { initialId: string; bodyType: BodyType; onSelect: (patternId: string | undefined) => void }) {
  const saved = loadPreferences();
  const [grouping, setGrouping] = createSignal(saved.grouping);
  // The body's own patterns come first; showing others (menswear on a feminine body, say) is kept until the body type changes.
  const bodyAudience = () => audienceForBody(props.bodyType);
  const [audience, setAudience] = createSignal(saved.audienceBody === props.bodyType ? saved.audience : bodyAudience());
  const [showFixtures, setShowFixtures] = createSignal(saved.showFixtures);
  const [selectedId, setSelectedId] = createSignal(saved.selected ?? props.initialId);
  const sections = createMemo(() => catalogueSections(grouping(), audience(), showFixtures()));
  const visible = createMemo(() => sections().flatMap((section) => section.subsections.flatMap((subsection) => subsection.entries)));
  const selected = createMemo(() => visible().find((entry) => entry.specification.pattern.id === selectedId()) ?? visible()[0]);
  // Pieces drafted with the pattern's defaults, modules' pieces included.
  const pieceCount = createMemo(() => {
    const entry = selected();
    try { return entry ? draftPattern(entry.specification, defaultsFor(entry.specification)).blocks.length : undefined; } catch { return undefined; }
  });
  createEffect(() => props.onSelect(selected()?.specification.pattern.id));
  createEffect(() => {
    const preferences: LibraryPreferences = { grouping: grouping(), audience: audience(), audienceBody: props.bodyType, showFixtures: showFixtures(), selected: selected()?.specification.pattern.id };
    try { localStorage.setItem(libraryStorageKey, JSON.stringify(preferences)); } catch { /* storage unavailable: keep in memory only */ }
  });

  let grid: HTMLDivElement | undefined;
  const tiles = () => [...(grid?.querySelectorAll<HTMLElement>(".select-tile") ?? [])];
  const select = (id: string, focus = false) => {
    setSelectedId(id);
    const tile = tiles().find((element) => element.dataset.patternId === id);
    tile?.scrollIntoView({ block: "nearest", inline: "nearest" });
    if (focus) tile?.focus({ preventScroll: true });
  };
  const open = (entry: CatalogueEntry | undefined) => { if (entry) navigate({ name: "pattern", patternId: entry.specification.pattern.id, view: "workspace" }); };

  const onKey = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    // Leave the keys to fields, and to anything open over the page such as the guide.
    if (target?.closest("input, select, textarea, dialog, [role=dialog]") || document.querySelector("dialog[open]")) return;
    if ((event.key === "Enter" || event.key === " ") && target?.classList.contains("select-tile")) {
      event.preventDefault();
      open(selected());
      return;
    }
    const direction = arrowKeys[event.key];
    if (!direction) return;
    event.preventDefault();
    const all = tiles();
    const current = all.find((element) => element.dataset.patternId === selected()?.specification.pattern.id);
    const next = current ? neighbour(all, current, direction) : all[0];
    if (next?.dataset.patternId) select(next.dataset.patternId, true);
  };
  onMount(() => {
    window.addEventListener("keydown", onKey);
    const id = selected()?.specification.pattern.id;
    if (id) tiles().find((element) => element.dataset.patternId === id)?.scrollIntoView({ block: "nearest" });
  });
  onCleanup(() => window.removeEventListener("keydown", onKey));

  const count = (audienceId: Audience | "all") => specifications.filter((entry) => {
    const collection = collectionFor(entry);
    return collection.id !== "fixtures" && (audienceId === "all" || collection.audience === audienceId);
  }).length;

  const bodyLabel = () => bodyTypes.find((type) => type.id === props.bodyType)?.label.toLowerCase() ?? props.bodyType;
  const audienceLabel = (id: Audience) => audiences.find((item) => item.id === id)!.label.toLowerCase();

  return (
    <main class="library select-screen">
      <section class="select-options" aria-label="Patterns">
        <div class="select-toolbar">
          <div class="select-heading"><p class="eyebrow">PATTERN LIBRARY</p><h2>Choose a pattern</h2></div>
          <div class="select-controls">
            <div class="segmented" role="group" aria-label="Group patterns by">
              <span>Group</span>
              <button classList={{ active: grouping() === "book" }} aria-pressed={grouping() === "book"} onClick={() => setGrouping("book")}>Book</button>
              <button classList={{ active: grouping() === "type" }} aria-pressed={grouping() === "type"} onClick={() => setGrouping("type")}>Type</button>
            </div>
            <div class="segmented" role="group" aria-label="Show patterns for">
              <span>Show</span>
              <button classList={{ active: audience() === "all" }} aria-pressed={audience() === "all"} onClick={() => setAudience("all")}>All</button>
              <For each={audiences}>{(item) => (
                <button classList={{ active: audience() === item.id }} aria-pressed={audience() === item.id} onClick={() => setAudience(item.id)}>{item.label} <small>{count(item.id)}</small></button>
              )}</For>
            </div>
            <label class="select-fixtures"><input type="checkbox" checked={showFixtures()} onChange={(event) => setShowFixtures(event.currentTarget.checked)} /> Test fixtures</label>
            <div class="segmented" role="group" aria-label="Ready-made groups">
              <span>Groups</span>
              <For each={printSets}>{(set) => <button type="button" title={`${set.description} Ticks them as your group.`} onClick={() => setGroup(set.members.map((member) => member.patternId))}>{set.name} <small>{set.members.length}</small></button>}</For>
            </div>
          </div>
        </div>
        {/* Which patterns suit the body from the measurements step, and the way out of (or back to) that filter. */}
        <div class="body-filter" classList={{ off: audience() !== bodyAudience() }} aria-live="polite">
          <Show when={audience() === bodyAudience()} fallback={<>
            <p>{audience() === "all" ? "Patterns for other bodies are shown too" : `These are ${audienceLabel(audience() as Audience)} patterns`}, so check the fit closely.</p>
            <button type="button" class="link-button" onClick={() => setAudience(bodyAudience())}>Show {audienceLabel(bodyAudience())} only</button>
          </>}>
            <p><Show when={count(bodyAudience()) > 0} fallback={<>No {audienceLabel(bodyAudience())} patterns yet for a <strong>{bodyLabel()}</strong> body. The book is being added.</>}>
              Showing {audienceLabel(bodyAudience())}.
            </Show></p>
            <button type="button" class="link-button" onClick={() => setAudience("all")}>Show all patterns</button>
          </Show>
        </div>
        {/* The patterns ticked to style, print and lay out together. */}
        <div class="group-bar" classList={{ empty: group().length === 0 }} aria-live="polite">
          <Show when={group().length > 0} fallback={<p>Tick patterns to style, print and lay them out together, or pick a ready-made group above.</p>}>
            <strong>{groupSet(group()).name}</strong>
            <ul>
              <For each={groupSet(group()).members}>{(member) => <li><b>{member.code}</b>{member.name}<button type="button" class="link-button" aria-label={`Take ${member.name} out of the group`} onClick={() => toggleInGroup(member.patternId)}>×</button></li>}</For>
            </ul>
            <div class="group-actions">
              <a class="button primary" href={routePath({ name: "set", setId: GROUP_ID, view: "workspace" })} onClick={linkClick}>Open together</a>
              <a class="button" href={routePath({ name: "set", setId: GROUP_ID, view: "print" })} onClick={linkClick}>Print</a>
              <a class="button" href={routePath({ name: "set", setId: GROUP_ID, view: "layout" })} onClick={linkClick}>Lay out</a>
              <button type="button" class="link-button" onClick={() => setGroup([])}>Clear</button>
            </div>
          </Show>
        </div>

        <div class="select-grid" ref={grid}>
          <For each={sections()}>{(section) => (
            <section class="select-section">
              <header class="select-section-title"><h3>{section.title}</h3><Show when={section.detail}><span>{section.detail}</span></Show></header>
              <Show when={section.subsections.length > 0} fallback={<p class="select-empty">No patterns from this book yet.</p>}>
                <For each={section.subsections}>{(subsection) => (
                  <div class="select-subsection">
                    <Show when={subsection.title}><h4>{subsection.title}</h4></Show>
                    <div class="select-tiles">
                      <For each={subsection.entries}>{(entry) => {
                        const id = entry.specification.pattern.id;
                        const isSelected = () => selected()?.specification.pattern.id === id;
                        const grouped = () => group().includes(id);
                        return (
                          <div class="select-tile-wrap" classList={{ grouped: grouped() }}>
                          <button
                            class="select-tile"
                            classList={{ selected: isSelected() }}
                            data-pattern-id={id}
                            tabIndex={isSelected() ? 0 : -1}
                            title={[entry.specification.pattern.name, ...(entry.specification.pattern.makes?.length ? [`Makes: ${entry.specification.pattern.makes.join(", ")}`] : [])].join("\n")}
                            aria-label={entry.specification.pattern.name}
                            aria-pressed={isSelected()}
                            onClick={() => select(id)}
                            onDblClick={() => open(entry)}
                          >
                            <PatternThumbnail specification={entry.specification} aspect={1} />
                          </button>
                          <button type="button" class="group-toggle" tabIndex={-1} aria-pressed={grouped()} aria-label={`${grouped() ? "Take out of" : "Add to"} the group: ${entry.specification.pattern.name}`} title={grouped() ? "In the group: click to take out" : "Add to the group"} onClick={() => toggleInGroup(id)}><Show when={grouped()}><CheckIcon size={13} /></Show></button>
                          </div>
                        );
                      }}</For>
                    </div>
                  </div>
                )}</For>
              </Show>
            </section>
          )}</For>
          <Show when={sections().length === 0}><p class="select-empty">No patterns match these filters.</p></Show>
        </div>
        <p class="select-hint"><kbd>←</kbd><kbd>↑</kbd><kbd>↓</kbd><kbd>→</kbd> select · <kbd>Enter</kbd> open</p>
      </section>

      <aside class="select-inspector" aria-live="polite">
        <Show when={selected()} fallback={<p class="select-empty">Choose a pattern to see it here.</p>}>
          {(entry) => {
            const pattern = () => entry().specification.pattern;
            const href = () => routePath({ name: "pattern", patternId: pattern().id, view: "workspace" });
            return (
              <>
                <div class="inspector-portrait">
                  {/* Keyed so the preview redrafts when the selection changes. */}
                  <For each={[entry()]}>{(current) => <PatternThumbnail specification={current.specification} aspect={1.3} />}</For>
                </div>
                <div class="inspector-body">
                  <p class="eyebrow">{collectionFor(entry()).name} · {garmentType(entry())}</p>
                  <h2>{pattern().name}</h2>
                  <dl class="inspector-facts">
                    <div><dt>Version</dt><dd>v{pattern().version}</dd></div>
                    <div><dt>Source</dt><dd>{collectionFor(entry()).source}</dd></div>
                    <div><dt>Pieces</dt><dd>{pieceCount() ?? "–"}</dd></div>
                  </dl>
                  <Show when={pattern().description}><p class="inspector-description">{pattern().description}</p></Show>
                  <Show when={pattern().makes?.length}>
                    <div class="inspector-makes">
                      <h3>Makes</h3>
                      <ul><For each={pattern().makes}>{(garment) => <li>{garment}</li>}</For></ul>
                    </div>
                  </Show>
                  <div class="inspector-actions">
                    <a class="button primary" href={href()} onClick={linkClick}>Open pattern</a>
                    <button type="button" aria-pressed={group().includes(pattern().id)} onClick={() => toggleInGroup(pattern().id)}>{group().includes(pattern().id) ? "Take out of group" : "Add to group"}</button>
                    <a class="button" href={routePath({ name: "measurements", patternId: pattern().id })} onClick={linkClick}>Measurements</a>
                  </div>
                </div>
              </>
            );
          }}
        </Show>
      </aside>
    </main>
  );
}
